import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { toast } from 'react-toastify';
import { getOwnerRobocaps, registerRobocap, registerRobocapById, unlinkRobocap } from '@services/owner';
import Spinner from '@util/spinner';

const RoboCapsView = () => {
	const router = useRouter();
	const [robocaps, setRobocaps] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [robocapCode, setRobocapCode] = useState('');
	const [robocapId, setRobocapId] = useState('');
	const [registrationMode, setRegistrationMode] = useState('id');
	const [isRegistering, setIsRegistering] = useState(false);
	const [unlinkingId, setUnlinkingId] = useState(null);

	const fetchRobocaps = async () => {
		try {
			const response = await getOwnerRobocaps();
			setRobocaps(response?.robocaps || []);
		} catch (error) {
			console.error('Error fetching robocaps:', error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchRobocaps();
	}, []);

	const handleRegister = async (e) => {
		e.preventDefault();
		const isCodeMode = registrationMode === 'code';
		const value = isCodeMode ? robocapCode.trim() : robocapId.trim();

		if (!value) {
			toast.error(isCodeMode ? 'Please enter a RoboCap code' : 'Please enter a RoboCap ID', { position: 'top-right' });
			return;
		}

		setIsRegistering(true);
		try {
			const response = isCodeMode
				? await registerRobocap(value)
				: await registerRobocapById(value);
			if (response?.error) {
				toast.error(response.error, { position: 'top-right' });
			} else if (response?.robocap) {
				toast.success('RoboCap registered successfully!', { position: 'top-right' });
				setRobocapCode('');
				setRobocapId('');
				fetchRobocaps();
			}
		} catch (error) {
			const errorMessage = error.response?.data?.error || 'Failed to register RoboCap';
			toast.error(errorMessage, { position: 'top-right' });
		} finally {
			setIsRegistering(false);
		}
	};

	const handleUnlink = async (e, robocap) => {
		e.stopPropagation();
		const confirmed = window.confirm(
			'Are you sure you want to unlink this robocap? Your upload history will be preserved but you will lose management access.'
		);
		if (!confirmed) return;

		setUnlinkingId(robocap.id);
		try {
			await unlinkRobocap(robocap.id);
			setRobocaps((prev) => prev.filter((r) => r.id !== robocap.id));
			toast.success('RoboCap unlinked successfully', { position: 'top-right' });
		} catch (error) {
			const errorMessage = error.response?.data?.error || 'Failed to unlink RoboCap';
			toast.error(errorMessage, { position: 'top-right' });
		} finally {
			setUnlinkingId(null);
		}
	};

	if (isLoading) {
		return (
			<Spinner
				loading={true}
				text="Loading..."
			/>
		);
	}

	return (
		<div className="w-full flex flex-col items-start gap-[16px]">
			<div className="w-full flex flex-col items-start gap-[16px]">
				<div className="flex flex-col justify-center items-start gap-[4px]">
					<p className="text-[#727272] text-[18px] not-italic font-medium leading-[28px] tracking-[-0.72px]">
						Register a RoboCap:
					</p>
				</div>
				<div className="flex gap-[24px]">
					<button
						type="button"
						onClick={() => setRegistrationMode('id')}
						className={`pb-[4px] text-[16px] font-medium tracking-[-0.64px] border-b-2 bg-transparent cursor-pointer ${
							registrationMode === 'id'
								? 'text-[#008735] border-[#008735]'
								: 'text-[#727272] border-transparent'
						}`}
					>
						By ID
					</button>
					<button
						type="button"
						onClick={() => setRegistrationMode('code')}
						className={`pb-[4px] text-[16px] font-medium tracking-[-0.64px] border-b-2 bg-transparent cursor-pointer ${
							registrationMode === 'code'
								? 'text-[#008735] border-[#008735]'
								: 'text-[#727272] border-transparent'
						}`}
					>
						By Code
					</button>
				</div>
				<div className="w-full flex flex-col sm:flex-row gap-4 items-start sm:items-center">
					{registrationMode === 'code' ? (
						<input
							type="text"
							value={robocapCode}
							onChange={(e) => setRobocapCode(e.target.value)}
							placeholder="Enter 3-word code (e.g., Apple Banana Cherry)"
							className="w-full sm:w-[400px] px-[16px] py-[12px] rounded-[12px] border-[1px] border-solid border-[#DDDDDD] bg-white text-[16px] focus:outline-none focus:border-[#008735]"
							disabled={isRegistering}
						/>
					) : (
						<input
							type="text"
							value={robocapId}
							onChange={(e) => setRobocapId(e.target.value)}
							placeholder="Enter 16-char hex ID (e.g., a1b2c3d4e5f60708)"
							className="w-full sm:w-[400px] px-[16px] py-[12px] rounded-[12px] border-[1px] border-solid border-[#DDDDDD] bg-white text-[16px] focus:outline-none focus:border-[#008735]"
							disabled={isRegistering}
						/>
					)}
					<button
						type="button"
						onClick={handleRegister}
						disabled={isRegistering || !(registrationMode === 'code' ? robocapCode.trim() : robocapId.trim())}
						className="flex pl-[12px] pr-[16px] py-[10px] items-center gap-[6px] rounded-[1000px] border-[1px] border-solid border-[#25BD66] bg-[#008735] disabled:opacity-50 disabled:cursor-not-allowed"
					>
						<p className="text-[#F3F3F3] text-[16px] not-italic font-semibold leading-[normal] tracking-[-0.64px]">
							{isRegistering ? 'Registering...' : 'Register'}
						</p>
					</button>
				</div>
			</div>

			{robocaps.length > 0 ? (
				<div className="w-full flex flex-col items-start gap-[16px]">
					<div className="flex flex-col justify-center items-start gap-[4px]">
						<p className="text-[#727272] text-[18px] not-italic font-medium leading-[28px] tracking-[-0.72px]">
							My RoboCaps:
						</p>
					</div>
					<div className="w-full flex flex-wrap gap-4 sm:gap-6 justify-center sm:justify-start">
						{robocaps.map((robocap) => (
							<div
								key={robocap.id}
								onClick={() => router.push(`/owner/robocaps/${robocap.id}`)}
								className="flex w-full sm:w-[280px] p-[16px] flex-col items-start gap-[12px] rounded-[16px] bg-white cursor-pointer hover:shadow-md transition-shadow"
							>
								<div className="flex items-center gap-[8px]">
									<div className="w-[12px] h-[12px] rounded-full bg-[#25BD66]" />
									<p className="text-[#000] text-[18px] not-italic font-semibold leading-[24px] tracking-[-0.72px]">
										{robocap.name}
									</p>
								</div>
								<p className="text-[#727272] text-[14px] not-italic font-medium leading-[20px] tracking-[-0.56px]">
									ID: {robocap.id}
								</p>
								<button
									type="button"
									onClick={(e) => handleUnlink(e, robocap)}
									disabled={unlinkingId === robocap.id}
									className="self-start px-[12px] py-[6px] rounded-[8px] border-[1px] border-solid border-[#DD3333] bg-transparent text-[#DD3333] text-[13px] font-medium tracking-[-0.52px] cursor-pointer hover:bg-[#DD3333] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{unlinkingId === robocap.id ? 'Unlinking...' : 'Unlink'}
								</button>
							</div>
						))}
					</div>
				</div>
			) : (
				<div className="flex w-full h-[160px] flex-col justify-center gap-[16px] rounded-[16px] bg-[rgba(221,_221,_221,_0.50)]">
					<div className="flex pl-0 pr-0 py-[40px] flex-col justify-center items-center gap-[16px]">
						<p className="text-[#868686] text-center text-[16px] not-italic font-medium leading-[20px] tracking-[-0.64px]">
							You have no RoboCaps registered yet.
						</p>
						<p className="text-[#868686] text-center text-[14px] not-italic font-normal leading-[18px] tracking-[-0.56px]">
							Enter your 3-word code or RoboCap ID above to register.
						</p>
					</div>
				</div>
			)}
		</div>
	);
};

export default RoboCapsView;
